import { useEffect, useContext } from 'react';
import { useConfigService, ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { getMcvisId } from '../utils/getUserInfo';

const GOOGLE_GARDEN_URL_PATH = '/commerce-sales-agent/get-token';
const FAILURE_ERROR_MESSAGE = 'There was an issue adding this item to your cart.';

export const useGoogleGardenAgent = () => {
  const { deliveryZip, hosts } = useContext(ExperienceContext);
  const thdCustomer = useThdCustomer();
  const storeId = useStoreId();
  const GOOGLE_GARDEN_SCRIPT_SRC = useConfigService('google_garden_agent_script_url');
  const fsEnableGoogleGardenAgent = useConfigService('google_garden_agent_enable');
  const googleGardenTokenKey = useConfigService('google_garden_agent_token_key');
  const GOOGLE_GARDEN_AGENT_ENTRY_PAGE_IDS = useConfigService('google_garden_agent_entry_pageids') || [];

  // Helper function to process cart success response
  const processCartSuccess = (res) => {
    // 500 errors are coming in errors object in cart response.
    if (res?.errors?.length) {
      return {
        success: false,
        message: res?.errors?.[0]?.message || FAILURE_ERROR_MESSAGE
      };
    }
    const cartModel = res?.CartModel;
    const cartErrors = Array.isArray(cartModel?.errorModel) ? cartModel.errorModel : [];
    const hasItems = (cartModel?.itemModels?.length ?? 0) > 0;

    if (cartErrors.length && !hasItems) {
      const message = cartErrors[0]?.description ?? FAILURE_ERROR_MESSAGE;
      return { success: false, message };
    }
    const response = { success: true };
    return response;
  };

  useEffect(() => {
    const pagePathSegments = typeof window !== 'undefined' ? window.location.pathname.split('/') : [];
    const pageId = pagePathSegments[pagePathSegments?.length - 1];
    const loadGoogleGardenAgent = fsEnableGoogleGardenAgent && GOOGLE_GARDEN_AGENT_ENTRY_PAGE_IDS.includes(pageId);

    const handleGoogleAgentLoaded = async () => {
      const { mcvisID, svocID: svocId = '', userID: userId = '' } = thdCustomer ?? {};
      const metadata = {
        zipCode: deliveryZip,
        storeId,
        mcvisId: getMcvisId(mcvisID),
        svocId,
        userId
      };
      const endpoint = `${hosts.apionline}${GOOGLE_GARDEN_URL_PATH}?key=${googleGardenTokenKey}`
        + `&mcvisId=${metadata.mcvisId}`;
      // Fetch session id from endpoint
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Error fetching session ID: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      const output = {
        sessionId: data?.sessionId,
        token: data?.token,
        metadata
      };
      return output;
    };

    const getHost = () => {
      const DEFAULT_HOST = 'https://www.homedepot.com';
      if (typeof window === 'undefined') return DEFAULT_HOST;

      const { hostname, origin } = window.location;
      return hostname.includes('localhost') ? DEFAULT_HOST : origin;
    };

    const handleAddToCart = async (itemId, quantity = '1') => {
      if (!itemId) {
        return {
          success: false,
          error: 'Invalid itemId'
        };
      }
      try {
        const endpoint = new URL('/federation-gateway/rest/proxy', getHost()).toString();
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json; charset=utf-8',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            CartRequest: {
              itemDetails: [{
                itemId,
                quantity,
              }],
            },
          }),
        });
        const data = await response.json();
        return processCartSuccess(data);
      } catch (error) {
        return {
          success: false,
          error: error?.message
        };
      }
    };

    let googleGardenScriptLoaded = {};
    if (!window.googleAgentSettings && deliveryZip && loadGoogleGardenAgent) {
      const scripts = Array.from(document.getElementsByTagName('script'));
      googleGardenScriptLoaded = scripts.find((script) => script.getAttribute('src') === GOOGLE_GARDEN_SCRIPT_SRC);

      window.googleAgentSettings = {
        id: 'js7812siwvz02nQa',
        tokenFunc: handleGoogleAgentLoaded,
        handleCartUpdate: handleAddToCart
      };

      if (!googleGardenScriptLoaded) {
        const script = document.createElement('script');
        script.src = GOOGLE_GARDEN_SCRIPT_SRC;
        script.type = 'text/javascript';
        script.async = true;
        document.head.insertBefore(script, document.head.firstChild);
      }
    }

    return () => {
      if (googleGardenScriptLoaded?.parentNode) {
        googleGardenScriptLoaded.parentNode.removeChild(googleGardenScriptLoaded);
      }
    };

  }, [deliveryZip, storeId, fsEnableGoogleGardenAgent, GOOGLE_GARDEN_AGENT_ENTRY_PAGE_IDS]);

  const openChat = () => {
    const messengerBubble = document.querySelector('chat-messenger-chat-bubble');
    if (messengerBubble) {
      messengerBubble.openChat();
    }
  };

  return [
    openChat
  ];
};